/**
 * TemplatePickerModal — the composer's "Templates" picker.
 *
 * A read-only popup (replacing the old cramped inline dropdown) for choosing a
 * reusable reply snippet. Templates are bucketed into tabs by `category`:
 *   - All           → everything available for the current channel
 *   - Welcome texts → category 'welcome' (intro / follow-up / re-engagement)
 *   - Study details → category 'study_details' (the AI-drafted, study-specific set)
 *   - General       → category 'general' (everything else; was previously hidden)
 * Each row also carries a scope badge — "This study" vs "Global" (network-wide) —
 * so recruiters can tell where a snippet comes from. Search + sort toggle.
 * Picking one calls onPick(template) and closes; the composer pre-fills the
 * message. Authoring/editing lives on the study builder's Templates tab.
 */
import { useEffect, useMemo, useState } from 'react';
import { X, Search, ArrowDownUp, Sparkles, FileText, Globe } from 'lucide-react';
import { MessageTemplate } from '../types';
import { ChannelChip, CategoryChip, TemplateCategory } from './studies/TemplateForm';
import { Z } from './ui';

/** Scope badge: a study-scoped snippet vs a network-wide (global) one. */
function ScopeChip({ studyScoped }: { studyScoped: boolean }) {
  return studyScoped ? (
    <span className="inline-flex items-center gap-1 rounded-full bg-primary-100 px-2 py-0.5 text-xs font-medium text-primary-700 dark:bg-primary-900/40 dark:text-primary-300">
      This study
    </span>
  ) : (
    <span className="inline-flex items-center gap-1 rounded-full bg-gray-100 px-2 py-0.5 text-xs font-medium text-gray-700 dark:bg-gray-700 dark:text-gray-300">
      <Globe className="h-3 w-3" /> Global
    </span>
  );
}

type Tab = 'all' | TemplateCategory;
type Sort = 'recent' | 'az';

interface Props {
  /** Every template the composer loaded (study + global); filtered here by channel. */
  templates: MessageTemplate[];
  /** The composer's active send channel — only sms/email templates ('any' always shown). */
  channel: 'sms' | 'email';
  /** Recipient label for the "To:" line (optional). */
  contactName?: string;
  onPick: (t: MessageTemplate) => void;
  onClose: () => void;
}

const TABS: { key: Tab; label: string }[] = [
  { key: 'all', label: 'All' },
  { key: 'welcome', label: 'Welcome texts' },
  { key: 'study_details', label: 'Study details' },
  { key: 'general', label: 'General' },
];

function tsOf(t: MessageTemplate): number {
  const d = Date.parse(t.updated_at || t.created_at || '');
  return Number.isNaN(d) ? 0 : d;
}

export default function TemplatePickerModal({ templates, channel, contactName, onPick, onClose }: Props) {
  const [tab, setTab] = useState<Tab>('all');
  const [query, setQuery] = useState('');
  const [sort, setSort] = useState<Sort>('recent');

  // Esc to close.
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') onClose(); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onClose]);

  // Templates usable on the current channel ('any' works for both).
  const channelTemplates = useMemo(
    () => templates.filter((t) => t.channel === 'any' || t.channel === channel),
    [templates, channel],
  );

  // Per-tab counts (channel-scoped) for the tab labels.
  const counts: Record<Tab, number> = useMemo(() => ({
    all: channelTemplates.length,
    welcome: channelTemplates.filter((t) => t.category === 'welcome').length,
    study_details: channelTemplates.filter((t) => t.category === 'study_details').length,
    general: channelTemplates.filter((t) => t.category === 'general').length,
  }), [channelTemplates]);

  const visible = useMemo(() => {
    const q = query.trim().toLowerCase();
    let list = channelTemplates;
    if (tab !== 'all') list = list.filter((t) => t.category === tab);
    if (q) list = list.filter((t) =>
      t.title.toLowerCase().includes(q)
      || t.body.toLowerCase().includes(q)
      || (t.subject || '').toLowerCase().includes(q));
    const sorted = [...list];
    sorted.sort((a, b) => (sort === 'az' ? a.title.localeCompare(b.title) : tsOf(b) - tsOf(a)));
    return sorted;
  }, [channelTemplates, tab, query, sort]);

  return (
    <div
      className={`fixed inset-0 ${Z.modalRaised} flex items-end justify-center bg-black/50 p-0 sm:items-center sm:p-4`}
      onClick={onClose}
    >
      <div
        className="flex max-h-[88vh] w-full max-w-2xl flex-col overflow-hidden rounded-t-xl bg-white shadow-xl focus:outline-none dark:bg-gray-800 sm:rounded-xl"
        onClick={(e) => e.stopPropagation()}
        role="dialog"
        aria-modal="true"
        aria-label="Message templates"
      >
        {/* Header */}
        <div className="flex items-start justify-between gap-3 border-b border-gray-100 p-4 dark:border-gray-700 sm:px-6 sm:pt-5">
          <div>
            <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100">Message Templates</h3>
            {contactName && (
              <p className="mt-0.5 text-sm text-gray-500 dark:text-gray-400">
                To: <span className="font-medium text-gray-700 dark:text-gray-200">{contactName}</span>
              </p>
            )}
          </div>
          <button
            type="button"
            onClick={onClose}
            aria-label="Close templates"
            className="-mr-1 rounded-lg p-1.5 text-gray-400 transition-colors duration-150 hover:bg-gray-100 hover:text-gray-600 focus:outline-none focus:ring-2 focus:ring-primary-500 dark:hover:bg-gray-700"
          >
            <X className="h-5 w-5" />
          </button>
        </div>

        {/* Search + sort */}
        <div className="flex items-center gap-2 px-4 pt-4 sm:px-6">
          <div className="relative flex-1">
            <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-gray-400" />
            <input
              type="text"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder="Search templates…"
              aria-label="Search templates"
              autoFocus
              className="w-full rounded-lg border border-gray-300 bg-white py-2 pl-9 pr-3 text-sm text-gray-900 placeholder-gray-400 focus:border-primary-500 focus:outline-none focus:ring-1 focus:ring-primary-500 dark:border-gray-600 dark:bg-gray-700 dark:text-gray-100 dark:placeholder-gray-400"
            />
          </div>
          <button
            type="button"
            onClick={() => setSort((s) => (s === 'recent' ? 'az' : 'recent'))}
            title="Toggle sort order"
            className="inline-flex shrink-0 items-center gap-1.5 rounded-lg border border-gray-300 px-3 py-2 text-sm font-medium text-gray-600 transition-colors duration-150 hover:bg-gray-50 dark:border-gray-600 dark:text-gray-300 dark:hover:bg-gray-700"
          >
            <ArrowDownUp className="h-4 w-4" />
            {sort === 'recent' ? 'Most Recent' : 'A–Z'}
          </button>
        </div>

        {/* Tabs */}
        <div className="mt-3 flex gap-1 overflow-x-auto border-b border-gray-200 px-4 dark:border-gray-700 sm:px-6">
          {TABS.map((t) => {
            const active = tab === t.key;
            return (
              <button
                key={t.key}
                type="button"
                onClick={() => setTab(t.key)}
                className={`-mb-px border-b-2 px-3 py-2 text-sm font-medium transition-colors duration-150 focus:outline-none ${
                  active
                    ? 'border-primary-600 text-primary-700 dark:border-primary-400 dark:text-primary-300'
                    : 'border-transparent text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200'
                }`}
              >
                {t.label}
                <span className={`ml-1.5 text-xs ${active ? 'text-primary-500 dark:text-primary-400' : 'text-gray-400'}`}>
                  {counts[t.key]}
                </span>
              </button>
            );
          })}
        </div>

        {/* List */}
        <div className="flex-1 overflow-y-auto px-2 py-2 sm:px-3">
          {visible.length === 0 ? (
            <div className="flex flex-col items-center justify-center py-12 text-center">
              <FileText className="mb-3 h-10 w-10 text-gray-300 dark:text-gray-600" />
              <p className="text-sm font-medium text-gray-900 dark:text-gray-100">No templates here</p>
              <p className="mt-1 max-w-xs text-sm text-gray-500 dark:text-gray-400">
                {query
                  ? 'No templates match your search.'
                  : tab === 'study_details'
                    ? 'Generate a study-specific set on the study’s Templates tab.'
                    : tab === 'welcome'
                      ? 'No welcome texts yet for this channel.'
                      : tab === 'general'
                        ? 'No general templates for this channel yet.'
                        : 'No templates available for this channel yet.'}
              </p>
            </div>
          ) : (
            <ul className="divide-y divide-gray-100 dark:divide-gray-700/60">
              {visible.map((t) => (
                <li key={t.id}>
                  <button
                    type="button"
                    onClick={() => onPick(t)}
                    className="group block w-full rounded-lg px-3 py-3 text-left transition-colors duration-150 hover:bg-gray-50 focus:bg-gray-50 focus:outline-none dark:hover:bg-gray-700/50 dark:focus:bg-gray-700/50"
                  >
                    <div className="flex flex-wrap items-center gap-1.5">
                      <span className="text-sm font-semibold text-gray-900 dark:text-gray-100">{t.title}</span>
                      <ChannelChip channel={t.channel} />
                      <ScopeChip studyScoped={!!t.study_id} />
                      {tab === 'all' && <CategoryChip category={t.category} />}
                      {t.source === 'ai' && (
                        <span className="inline-flex items-center gap-1 rounded-full bg-primary-100 px-2 py-0.5 text-xs font-medium text-primary-700 dark:bg-primary-900/40 dark:text-primary-300">
                          <Sparkles className="h-3 w-3" /> AI
                        </span>
                      )}
                    </div>
                    {t.subject && (
                      <p className="mt-1 truncate text-xs font-medium text-gray-600 dark:text-gray-300">Subject: {t.subject}</p>
                    )}
                    <p className="mt-1 line-clamp-2 text-sm text-gray-500 dark:text-gray-400">{t.body}</p>
                  </button>
                </li>
              ))}
            </ul>
          )}
        </div>
      </div>
    </div>
  );
}
